import { ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import {
  PaymentStatus,
  StripePaymentIntentStatus,
  type PaymentHandler,
  type PaymentHandlerWithTab,
  type PaymentStatusResponse,
} from '@/types/payment';
import { REDIRECT_PAYMENT_POLLING_INTERVAL } from '@/constants/payment';
import { sleep } from '@/utils/payment';
import { InternalMessageEventsName } from '@/constants/iframeMessages';
import { getNuveiPaymentStatus } from '@/utils/nuvei';
import { mapAdyenResultCodeToPaymentStatus } from '@/utils/adyen';

class StripePaymentHandler implements PaymentHandler {
  async poll(
    clientSecret: string,
    __: (redirectResult: string) => Promise<PaymentStatusResponse>,
    ___: string,
    isTabClosed: boolean
  ): Promise<{ status: PaymentStatus; params: Record<string, unknown> }> {
    const stripe = stripeService.getStripe();
    const result = await stripe.retrievePaymentIntent(clientSecret);
    const intentStatus = result?.paymentIntent?.status;

    if (intentStatus === StripePaymentIntentStatus.SUCCEEDED) {
      return { status: PaymentStatus.SUCCEEDED, params: result };
    }

    if (intentStatus === StripePaymentIntentStatus.REQUIRES_PAYMENT_METHOD) {
      return { status: PaymentStatus.FAILED, params: result };
    }

    if (isTabClosed) {
      return { status: PaymentStatus.CANCELLED, params: result };
    }

    return { status: PaymentStatus.PENDING, params: result };
  }
}

class NuveiPaymentHandler implements PaymentHandler {
  async poll(
    clientSecret: string,
    __: (redirectResult: string) => Promise<PaymentStatusResponse>,
    paymentMethodId: string,
    isTabClosed: boolean
  ): Promise<{ status: PaymentStatus; params: Record<string, unknown> }> {
    try {
      if (!isTabClosed) {
        return { status: PaymentStatus.PENDING, params: {} };
      }
      const paymentStatusReponse = await getNuveiPaymentStatus(clientSecret, paymentMethodId);
      if (!paymentStatusReponse) {
        return { status: PaymentStatus.FAILED, params: {} };
      }
      if (
        paymentStatusReponse.status === 'ERROR' ||
        paymentStatusReponse.transactionStatus === 'ERROR' ||
        paymentStatusReponse.transactionStatus === 'DECLINED'
      ) {
        return { status: PaymentStatus.FAILED, params: {} };
      } else if (paymentStatusReponse.transactionStatus === 'APPROVED') {
        return { status: PaymentStatus.SUCCEEDED, params: {} };
      } else if (paymentStatusReponse.transactionStatus === 'PENDING') {
        return { status: PaymentStatus.PENDING, params: {} };
      }
      return { status: PaymentStatus.PENDING, params: {} };
    } catch (error: unknown) {
      return { status: PaymentStatus.FAILED, params: error as Record<string, unknown> };
    }
  }
}

class AdyenPaymentHandler implements PaymentHandler {
  private tab: Window | null = null;
  private lastTabUrl: string = '';
  private boundMessageHandler: ((event: MessageEvent) => void) | null = null;

  setTab(tab: Window | null): void {
    this.tab = tab;

    if (this.tab) {
      this.boundMessageHandler = this.handleTabMessage.bind(this);
      window.addEventListener('message', this.boundMessageHandler);
    }
  }

  private handleTabMessage(event: MessageEvent): void {
    if (event.source === this.tab) {
      console.log('handleTabMessage', event);
      this.lastTabUrl = event.data.url;
    }
  }

  private extractRedirectResult(): string | null {
    let redirectResult = new URLSearchParams(this.tab?.location.search).get('redirectResult');

    if (!redirectResult && this.lastTabUrl) {
      try {
        const urlParams = new URLSearchParams(new URL(this.lastTabUrl).search);
        redirectResult = urlParams.get('redirectResult');
      } catch {
        return null;
      }
    }

    return redirectResult;
  }

  async poll(
    _: string,
    pollPaymentStatus: (redirectResult: string) => Promise<PaymentStatusResponse>,
    ___: string,
    isTabClosed: boolean
  ): Promise<{ status: PaymentStatus; params: Record<string, unknown> }> {
    if (!isTabClosed) {
      return { status: PaymentStatus.PENDING, params: {} };
    }

    let redirectResult: string | null = null;
    try {
      redirectResult = this.extractRedirectResult();
    } catch {
      return { status: PaymentStatus.CANCELLED, params: {} };
    }

    if (redirectResult) {
      const { resultCode } = await pollPaymentStatus(redirectResult);
      return { status: mapAdyenResultCodeToPaymentStatus(resultCode), params: { resultCode } };
    }
    return { status: PaymentStatus.CANCELLED, params: {} };
  }

  cleanup(): void {
    if (this.boundMessageHandler) {
      window.removeEventListener('message', this.boundMessageHandler);
    }
    this.tab = null;
    this.lastTabUrl = '';
    this.boundMessageHandler = null;
  }
}

export class RedirectPaymentManager {
  private tab: Window | null = null;
  private polling = false;
  private handler: PaymentHandlerWithTab | null = null;

  constructor(private provider: ProviderEnum) {
    this.initializeHandler();
  }

  private initializeHandler(): void {
    switch (this.provider) {
      case ProviderEnum.STRIPE:
        this.handler = new StripePaymentHandler();
        break;
      case ProviderEnum.ADYEN:
        this.handler = new AdyenPaymentHandler();
        break;
      case ProviderEnum.NUVEI:
        this.handler = new NuveiPaymentHandler();
        break;
      default:
        console.error(`Unsupported provider: ${this.provider}`);
    }
  }

  setTab(tab: Window | null): void {
    this.tab = tab;

    if (this.handler?.setTab) {
      this.handler.setTab(this.tab);
    }
  }

  sendRedirectMessage(redirectUrl: string): void {
    if (this.tab) {
      this.tab.postMessage({ event: InternalMessageEventsName.APM_REDIRECT, params: { url: redirectUrl } }, '*');
    }
  }

  openTab(url: string): void {
    this.tab = window.open(url, '_blank');

    if (this.handler?.setTab) {
      this.handler.setTab(this.tab);
    }

    window.parent.focus();
  }

  closeTab(): void {
    if (this.tab && !this.tab.closed) {
      this.tab.postMessage({ event: InternalMessageEventsName.APM_REDIRECT_CLOSE, params: {} }, '*');
      this.tab.close();
    }
    this.tab = null;
  }

  isTabClosed(): boolean {
    return this.tab?.closed ?? true;
  }

  async startPolling(
    clientSecret: string,
    onSuccess: (params: Record<string, unknown>) => void,
    onFailure: (error: string, params: Record<string, unknown>) => void,
    onCancel: (params: Record<string, unknown>) => void,
    pollPaymentStatus: (redirectResult: string) => Promise<PaymentStatusResponse>,
    paymentMethodId: string
  ): Promise<void> {
    if (!this.handler) {
      onFailure('Payment handler not initialized', {});
      return;
    }

    this.polling = true;

    while (this.polling) {
      await sleep(REDIRECT_PAYMENT_POLLING_INTERVAL);

      const data = await this.handler.poll(clientSecret, pollPaymentStatus, paymentMethodId, this.isTabClosed());

      if (data.status === PaymentStatus.SUCCEEDED) {
        this.handleSuccess(onSuccess, data.params);
        break;
      }

      if (data.status === PaymentStatus.FAILED) {
        this.handleFailure(onFailure, data.params);
        break;
      }

      if (data.status === PaymentStatus.CANCELLED) {
        this.handleCancellation(onCancel, data.params);
        break;
      }
    }
  }

  private handleSuccess(onSuccess: (params: Record<string, unknown>) => void, params: Record<string, unknown>): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onSuccess(params as Record<string, string>);
  }

  private handleFailure(
    onFailure: (error: string, params: Record<string, unknown>) => void,
    params: Record<string, unknown>
  ): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onFailure('Payment was declined', params);
  }

  private handleCancellation(
    onCancel: (params: Record<string, unknown>) => void,
    params: Record<string, unknown>
  ): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onCancel(params);
  }

  cleanup(): void {
    this.polling = false;
    this.closeTab();

    if (this.handler?.cleanup) {
      this.handler.cleanup();
    }
  }
}
